/**
 * Fehlzeiten (Absences) Endpoints
 *
 * Injected into `attendanceApi` from `attendanceApiBase.ts`.
 * Covers: Fehlzeiten CRUD, Klassenuebersicht, Student Absence Details.
 *
 * @module attendance/api/fehlzeitenEndpoints
 */

import { attendanceApi } from "./attendanceApiBase";
import {
  fetchFehlzeitenUnified,
  fetchFehlzeitById,
  createAbsence,
  updateAbsence,
  deleteAbsence,
  fetchKlassenuebersicht,
  fetchStudentAbsenceDetails,
  fetchSemesters,
  toIsoDate,
  parseTimeRange,
  type FetchFehlzeitenParams,
  type CreateAbsenceParams,
  type SemesterData,
} from "../../absence-tracking/shared/api";

// ============================================================================
// Injected Endpoints
// ============================================================================

const fehlzeitenEndpointsApi = attendanceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // Fehlzeiten (Absences) - Uses Service Layer
    // ========================================================================

    getFehlzeiten: builder.query<
      {
        items: unknown[];
        total: number;
        kbStudentCount: number;
        distinctClasses: { id: string; name: string }[];
        allowedClassIds?: string[];
      },
      FetchFehlzeitenParams
    >({
      async queryFn(args) {
        try {
          const result = await fetchFehlzeitenUnified(args);
          return { data: result };
        } catch (error) {
          return { error };
        }
      },
      providesTags: () => [{ type: "Fehlzeiten", id: "LIST" }],
    }),

    getFehlzeitById: builder.query<Record<string, unknown> | null, string>({
      async queryFn(fehlzeitId) {
        try {
          if (!fehlzeitId) return { data: null };
          const data = await fetchFehlzeitById(fehlzeitId);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, id) => [
        { type: "Fehlzeiten", id },
        { type: "AbsenceNotes", id },
      ],
    }),

    createFehlzeiten: builder.mutation<
      void,
      {
        entries: CreateAbsenceParams[];
        schoolId: string;
        profileId: string;
        roleLabel: string;
        recurrence?: { startDate: string; endDate: string; weekDays: number[] };
        forceOverwrite?: boolean;
      }
    >({
      async queryFn({
        entries,
        schoolId,
        profileId,
        recurrence,
        forceOverwrite,
      }) {
        try {
          const firstEntryRecurrence = (entries[0] as any)?._recurrence;
          const effectiveRecurrence = recurrence || firstEntryRecurrence;

          if (
            effectiveRecurrence &&
            effectiveRecurrence.startDate &&
            effectiveRecurrence.endDate
          ) {
            const firstEntry = entries[0];
            if (!firstEntry) throw new Error("No entries provided");

            const { createAbsenceWithRecurrence } = await import(
              "../../absence-tracking/shared/api/mutations"
            );

            const entryDate = firstEntry.date.includes(".")
              ? toIsoDate(firstEntry.date)
              : firstEntry.date;

            const result = await createAbsenceWithRecurrence({
              studentId: firstEntry.studentId,
              schoolId: schoolId,
              date: entryDate,
              status: firstEntry.status,
              reason: firstEntry.reason || null,
              startTime:
                firstEntry.startTime || (firstEntry as any).timeFrom || null,
              endTime: firstEntry.endTime || (firstEntry as any).timeTo || null,
              createdBy: profileId || null,
              recurrence: {
                startDate: effectiveRecurrence.startDate,
                endDate: effectiveRecurrence.endDate,
                repeatEveryUnit: "day",
                weekDays: effectiveRecurrence.weekDays || [],
              },
              forceOverwrite,
            });

            if (!result.success) {
              if (
                result.error === "DUPLICATE_ABSENCE_CONFLICT" &&
                result.conflicts
              ) {
                return {
                  error: {
                    message: "DUPLICATE_ABSENCE_CONFLICT",
                    conflicts: result.conflicts,
                  },
                };
              }
              throw new Error(result.error);
            }
            return { data: undefined };
          }

          for (const e of entries) {
            const result = await createAbsence({ ...e, forceOverwrite });
            if (!result.success) {
              if (
                result.error === "DUPLICATE_ABSENCE_CONFLICT" &&
                result.conflicts
              ) {
                return {
                  error: {
                    message: "DUPLICATE_ABSENCE_CONFLICT",
                    conflicts: result.conflicts,
                  },
                };
              }
              throw new Error(result.error);
            }
          }
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [
        "Fehlzeiten",
        "AbsenceNotes",
        "DailyLog",
        "AttendanceBundle",
      ],
    }),

    updateFehlzeit: builder.mutation<
      void,
      { original: Record<string, unknown>; update: Record<string, unknown> }
    >({
      async queryFn({ original, update }) {
        try {
          const studentId = (update.studentId || original.studentId) as string;
          const status = (update.status || original.status) as string;
          const dateIso = toIsoDate(
            (update.date || original.recordDateIso || original.date) as string,
          );
          const reason =
            update.reason !== undefined
              ? (update.reason as string)
              : (original.reason as string);
          const endDateIso = update.isMultiDay
            ? toIsoDate((update.endDate || dateIso) as string)
            : undefined;
          const { startTime, endTime } = parseTimeRange(
            (update.dateRange || original.dateRange) as string,
          );

          const result = await updateAbsence({
            studentId,
            date: dateIso,
            status,
            reason,
            endDate: endDateIso,
            absenceNoteId: original.absenceNoteId as string,
            startTime,
            endTime,
          });
          if (!result.success) throw new Error(result.error);
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [
        "Fehlzeiten",
        "AbsenceNotes",
        "DailyLog",
        "AttendanceBundle",
      ],
    }),

    deleteFehlzeit: builder.mutation<
      void,
      { studentId: string; date: string; status: string }
    >({
      async queryFn({ studentId, date, status }) {
        try {
          const result = await deleteAbsence({ studentId, date, status });
          if (!result.success) throw new Error(result.error);
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: [
        "Fehlzeiten",
        "AbsenceNotes",
        "DailyLog",
        "AttendanceBundle",
      ],
    }),

    // ========================================================================
    // Klassenuebersicht
    // ========================================================================

    getKlassenuebersichtComplete: builder.query<
      unknown[],
      {
        classId: string;
        startDate: string;
        endDate: string;
        includeFullDayLessons?: boolean;
        includeZeugnisExcluded?: boolean;
      }
    >({
      async queryFn({
        classId,
        startDate,
        endDate,
        includeFullDayLessons = true,
        includeZeugnisExcluded = false,
      }) {
        try {
          const data = await fetchKlassenuebersicht({
            classId,
            startDate,
            endDate,
            includeFullDayLessons,
            includeZeugnisExcluded,
          });
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        {
          type: "Fehlzeiten",
          id: `klassenuebersicht-${args.classId}-${args.includeFullDayLessons}`,
        },
      ],
      keepUnusedDataFor: 120,
    }),

    getStudentAbsenceDetails: builder.query<
      unknown[],
      {
        studentId: string;
        startDate: string;
        endDate: string;
        detailType: "days" | "hours" | "lateness";
        includeFullDayLessons?: boolean;
      }
    >({
      async queryFn({
        studentId,
        startDate,
        endDate,
        detailType,
        includeFullDayLessons = true,
      }) {
        try {
          const data = await fetchStudentAbsenceDetails(
            studentId,
            startDate,
            endDate,
            detailType,
            includeFullDayLessons,
          );
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        {
          type: "Fehlzeiten",
          id: `details-${args.studentId}-${args.detailType}-${args.includeFullDayLessons}`,
        },
      ],
      keepUnusedDataFor: 60,
    }),
    getSemesters: builder.query<SemesterData[], string>({
      async queryFn(schoolId) {
        try {
          if (!schoolId) return { data: [] };
          const data = await fetchSemesters(schoolId);
          return { data };
        } catch (error) {
          return { error: { status: "CUSTOM_ERROR", error: String(error) } };
        }
      },
      providesTags: (_r, _e, schoolId) => [
        // Using "Fehlzeiten" tag as base since "Semesters" isn't explicitly defined in api tagTypes globally
        { type: "Fehlzeiten", id: `semesters-${schoolId}` },
      ],
      keepUnusedDataFor: 300,
    }),
  }),
  overrideExisting: false,
});

// ============================================================================
// Generated Hooks
// ============================================================================

export const {
  useGetFehlzeitenQuery,
  useGetFehlzeitByIdQuery,
  useLazyGetFehlzeitByIdQuery,
  useCreateFehlzeitenMutation,
  useUpdateFehlzeitMutation,
  useDeleteFehlzeitMutation,
  useGetKlassenuebersichtCompleteQuery,
  useGetStudentAbsenceDetailsQuery,
  useLazyGetStudentAbsenceDetailsQuery,
  useGetSemestersQuery,
} = fehlzeitenEndpointsApi;
